// CsucIntegration.js
const ClickToPay = require('./ClickToPay');
const { elements } = require('../variables');
const {
  getClickToPayHtml,
  checkResponseStatusSuccess,
  checkResponseStatusCanceled,
  checkResponseStatusFailed,
  checkResponseStatusError,
  decodeJWT,
} = require('../util');
const { ctpSumbitFetch } = require('../http');
const { CSUC_CONFIG } = require('../constants');
const { mainConfigService } = require('../services/config');
class CsucIntegration extends ClickToPay {
  
  constructor(submitUrl, paymentMethod, currScript, fieldsService, documentCheckout, lifecycleHooks = {}) {
    super(submitUrl, paymentMethod, currScript, fieldsService);
    this.documentCheckout = documentCheckout;
    this.vendorSdkConfig = undefined;
    this.configCache = new Map(); // Cache API responses by submit_url

    this.hooks = {
      onPaymentButtonsReady: lifecycleHooks.onPaymentButtonsReady || (() => {
        this._hideLoader();
      }),
      onPaymentSuccess: lifecycleHooks.onPaymentSuccess || ((response) => {
        if (response.redirect_url) {
          this.callbackService.callBackPayment({ redirect_url: response.redirect_url });
        } else {
          this.callbackService.callBackSuccess(response);
        }
      }),
      onPaymentCanceled: lifecycleHooks.onPaymentCanceled || ((response) => {
        this.callbackService.callBackCancel(response);
      }),
      onPaymentError: lifecycleHooks.onPaymentError || ((error) => {
        this.callbackService.callBackError(error);
      }),
      onSDKInitError: lifecycleHooks.onSDKInitError || (() => {}),
    };
  }

  _triggerHook(hookName, ...args) {
    try {
      if (this.hooks[hookName]) {
        this.hooks[hookName](...args);
      }
    } catch (error) {
      console.warn(`Error in lifecycle hook '${hookName}':`, error);
    }
  }

  updateSubmitUrl(newSubmitUrl) {
    this.submitUrl = newSubmitUrl;
  }

  isInitialized() {
    return this.configCache.has(this.submitUrl);
  }

  async initialize() {
    // Check if we have cached config for this submit_url
    if (this.isInitialized()) {
      const cachedConfig = this.configCache.get(this.submitUrl);
      await this.handlePayment(cachedConfig);
      return;
    }

    // New method - fetch from API
    const response = await this._performInitialization();

    // Cache the response
    this.configCache.set(this.submitUrl, response);

    await this.handlePayment(response);
  }

  async handlePayment(vendorSdkConfig) {
    this.vendorSdkConfig = vendorSdkConfig;
    this.render();
  }

  _hideLoader() {
    const loaderSpan = this.documentCheckout
      .querySelector(`#${elements.blockClickToPayButton}`)
      ?.querySelector('.skeleton-loader');

    if (loaderSpan) {
      loaderSpan.classList.add('d-none'); // Add 'd-none' to hide
    }
  }

  _waitForPaymentButtons() {
    return new Promise((resolve) => {
      const checkButtons = () => {
        const paymentListContainer = document.getElementById(CSUC_CONFIG.CONTAINERS.PAYMENT_LIST);
        const embeddedPaymentContainer = document.getElementById(CSUC_CONFIG.CONTAINERS.EMBEDDED_PAYMENT);

        const paymentListHeight = paymentListContainer?.offsetHeight || 0;
        const embeddedPaymentHeight = embeddedPaymentContainer?.offsetHeight || 0;

        if (paymentListHeight > 50 || embeddedPaymentHeight > 50) {
          resolve();
        } else {
          setTimeout(checkButtons, 100);
        }
      };

      checkButtons();
    });
  }

  _createContainer(id) {
    const container = document.createElement('div');
    container.id = id;
    return container;
  }

  _createContainers() {
    // CyberSource SDK requires containers in regular DOM (outside Shadow DOM)
    // All styling handled by checkout-external.css
    const parentElement = document.getElementById(mainConfigService.get('selector'));
    const shadowWrapper = parentElement.querySelector('#ottu-checkout-shadow-wrapper');

    const paymentContainer = this._createContainer(CSUC_CONFIG.CONTAINERS.PAYMENT_LIST);
    const paymentButtonsList = this._createContainer(CSUC_CONFIG.CONTAINERS.EMBEDDED_PAYMENT);

    // Insert after shadow wrapper for proper layout flow
    if (shadowWrapper) {
      shadowWrapper.insertAdjacentElement('afterend', paymentButtonsList);
      shadowWrapper.insertAdjacentElement('afterend', paymentContainer);
    } else {
      parentElement.appendChild(paymentContainer);
      parentElement.appendChild(paymentButtonsList);
    }

    return { paymentContainer, paymentButtonsList };
  }

  _getClickToPayContainer() {
    return this.documentCheckout.querySelector(
      `#${elements.blockClickToPayButton}`
    );
  }

  _showClickToPayContainer() {
    const clickToPayContainerElement = this._getClickToPayContainer();
    const containerDiv = clickToPayContainerElement?.querySelector('div');

    if (containerDiv) {
      containerDiv.classList.remove('d-none');
    }

    return containerDiv;
  }

  _injectClickToPayHtml() {
    // Only inject if not already present (prevents duplication)
    if (!this.documentCheckout.querySelector(`#${elements.blockClickToPayButton}`)) {
      this.documentCheckout.querySelector(
        `#${elements.blockButtons}`
      ).innerHTML += getClickToPayHtml();
    }
  }

  _getShowArgs() {
    return {
      containers: {
        paymentSelection: CSUC_CONFIG.SELECTORS.PAYMENT_SELECTION,
        paymentScreen: CSUC_CONFIG.SELECTORS.PAYMENT_SCREEN,
      },
    };
  }

  async _initializeUnifiedPayments(accept) {
    this.acceptInstance = accept;
    return accept.unifiedPayments(false);
  }

  async _showPaymentUI(unifiedPayments, containerDiv) {
    containerDiv.classList.remove('d-none');
    window.upInstance = unifiedPayments;

    const showArgs = this._getShowArgs();

    // Show payment buttons
    const showPromise = unifiedPayments.show(showArgs);

    // Wait for payment buttons to appear in DOM, then trigger lifecycle hook
    this._waitForPaymentButtons().then(() => {
      this._triggerHook('onPaymentButtonsReady');
    });

    return showPromise;
  }

  async _processPayment(completeMandateResponse) {
    await this.callbackService.callBackPayment();
    
    let decodedPaymentData;
    try {
      decodedPaymentData = decodeJWT(completeMandateResponse);
    } catch (error) {
      console.error('[CSUC] Failed to decode JWT:', error);
      throw new Error('Failed to process payment: Invalid token format');
    }

    const response = await ctpSumbitFetch(
      this.vendorSdkConfig.form_config.action,
      this.vendorSdkConfig.form_config.method,
      decodedPaymentData
    );

    await this._handlePaymentResponse(response);
  }

  async _handlePaymentResponse(response) {
    this.fieldsService.disableEnableFields(false);

    // Check if response has WebSocket information
    if (response.ws_url && response.reference_number) {
      await this.handleWebSocket(response);
      return;
    }

    // Check response status and trigger appropriate hook
    if (checkResponseStatusSuccess(response)) {
      this._triggerHook('onPaymentSuccess', response);
    } else if (checkResponseStatusCanceled(response) || checkResponseStatusFailed(response)) {
      this._triggerHook('onPaymentCanceled', response);
    } else if (checkResponseStatusError(response)) {
      this._triggerHook('onPaymentError', response);
    } else {
      // Default to success if status is not explicitly defined
      this._triggerHook('onPaymentSuccess', response);
    }
  }

  _handlePaymentError(err) {
    this.fieldsService.disableEnableFields(false);

    // Normalize error format
    let errorObject = err;
    if (err.status >= 500) {
      errorObject = {
        status: 'error',
        message: 'Oops, something went wrong. Refresh the page and try again.',
      };
    }

    // Handle promise-based errors
    if (typeof err === 'object' && typeof err.then === 'function') {
      err.then((error) => {
        this._triggerHook('onPaymentError', error);
      });
    } else {
      this._triggerHook('onPaymentError', errorObject);
    }
  }

  _handleRenderError(err) {
    console.error('CyberSource SDK initialization failed:', err);
    this._hideLoader();
    const containerElement = this._getClickToPayContainer();
    containerElement?.querySelector('span')?.classList.add('d-none');

    this._triggerHook('onSDKInitError', err);
  }

  checkDependency() {
    // Check if CyberSource Accept SDK is available
    // In single-spa/SystemJS environments, it's cached as CyberSourceAccept
    return (
      typeof window.CyberSourceAccept === 'function' ||
      typeof window.Accept === 'function'
    );
  }

  async render() {
    // Clean up previous render before creating new one
    this._removeContainers();

    this._injectClickToPayHtml();
    this._createContainers();
    const containerDiv = this._showClickToPayContainer();

    try {
      const Accept = await this.loadVendorSdk();
      const accept = await Accept(this.vendorSdkConfig.capture_context);
      const unifiedPayments = await this._initializeUnifiedPayments(accept);
      const transientToken = await this._showPaymentUI(unifiedPayments, containerDiv);
      const completeMandateResponse = await unifiedPayments.complete(transientToken);
      await this._processPayment(completeMandateResponse);
    } catch (err) {
      if (err.status !== undefined) {
        this._handlePaymentError(err);
      } else {
        this._handleRenderError(err);
      }
    }
  }

  _removeContainers() {
    [CSUC_CONFIG.CONTAINERS.PAYMENT_LIST, CSUC_CONFIG.CONTAINERS.EMBEDDED_PAYMENT]
      .forEach(id => document.getElementById(id)?.remove());
  }

  _resetUI() {
    const clickToPayContainerElement = this._getClickToPayContainer();

    if (clickToPayContainerElement) {
      // Remove the entire Click-to-Pay container (not just reset its content)
      clickToPayContainerElement.remove();
    }
  }

  _clearReferences() {
    this.acceptInstance = null;
    if (window.upInstance) {
      window.upInstance = null;
    }
  }

  destroy() {
    this._removeContainers();
    this._resetUI();
    this._clearReferences();
  }

  reRender() {
    this.destroy();
    this.render();
  }

  loadVendorSdk() {
    return new Promise((resolve, reject) => {
      // Check if already loaded and cached
      if (
        window.CyberSourceAccept &&
        typeof window.CyberSourceAccept === 'function'
      ) {
        resolve(window.CyberSourceAccept);
        return;
      }

      const script = document.createElement('script');
      script.src = this.vendorSdkConfig.sdk_client.src;
      script.integrity = this.vendorSdkConfig.sdk_client.integrity;
      script.crossOrigin = 'anonymous';

      script.onload = () => {
        // The CyberSource SDK uses UMD pattern. In single-spa environments, SystemJS
        // intercepts the AMD define() call. We use System.import() to retrieve the module.
        if (window.System && typeof window.System.import === 'function') {
          window.System.import(this.vendorSdkConfig.sdk_client.src)
            .then((module) => {
              // Extract the Accept function from the module
              const CyberSourceAccept =
                module.Accept ||
                (typeof module === 'function' ? module : null) ||
                Object.values(module).find((val) => typeof val === 'function');

              if (
                CyberSourceAccept &&
                typeof CyberSourceAccept === 'function'
              ) {
                window.CyberSourceAccept = CyberSourceAccept;
                resolve(CyberSourceAccept);
              } else {
                reject(
                  new Error(
                    'CyberSource Accept SDK loaded but Accept function not found'
                  )
                );
              }
            })
            .catch((err) => {
              console.error(
                'Failed to import CyberSource SDK from SystemJS:',
                err
              );
              reject(err);
            });
        } else {
          // Plain HTML environment - SDK exports to window.Accept
          if (window.Accept && typeof window.Accept === 'function') {
            window.CyberSourceAccept = window.Accept;
            resolve(window.Accept);
          } else {
            reject(new Error('CyberSource Accept SDK failed to initialize'));
          }
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load CyberSource SDK'));
      };

      document.head.appendChild(script);
    });
  }
}

module.exports = CsucIntegration;
